import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Card,
} from "@mui/material";
import { UserRole } from "../../context/ConfigContext/types";
import { confirmAlert } from "react-confirm-alert";
import { UserWithClients } from "../../context/ClientAccessContext/types";
import { AdminPanelClient } from "../TopicsPanel/types";

export interface MainUserFormProps {
  item: UserWithClients;
  clients?: AdminPanelClient[];
  onUserUpdate?: (updatedUser: UserWithClients, updateMainDb: boolean) => void;
  onUserDelete?: (id: number, updateMainDb: boolean) => void;
}

enum Action {
  CREATE = "Create",
  UPDATE = "Update",
  DELETE = "Delete",
}

const UserForm: React.FC<MainUserFormProps> = ({ item: user, clients, onUserUpdate, onUserDelete }) => {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [email, setEmail] = useState(user.email);
  const [adObjectId, setAdObjectId] = useState(user.adObjectId);
  const [selectedClients, setSelectedClients] = useState(user.clientAccesses.map((access) => access.clientId));
  const [role, setRole] = useState(user.globalRole);
  const [updateMainDb, setUpdateMainDb] = useState(false);

  useEffect(() => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setAdObjectId(user.adObjectId);
    setSelectedClients(user.clientAccesses.map((access) => access.clientId));
    setRole(user.globalRole);
    setUpdateMainDb(false);
  }, [user]);

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleAdObjectIdChange = (e) => {
    setAdObjectId(e.target.value);
  };

  const handleTopicChange = (e) => {
    setSelectedClients(e.target.value);
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
  };

  const formValid = () => {
    return [firstName, lastName, email, adObjectId].some((value) => !value);
  };

  const handleSave = () => {
    const updatedUser: UserWithClients = {
      ...user,
      firstName,
      lastName,
      email,
      globalRole: role,
      adObjectId,
      clientAccesses: selectedClients.map((clientId) => {
        const client = clients.find((c) => c.id === clientId);
        return {
          id: clientId,
          role: UserRole.READER,
          clientId: client?.id,
          clientName: client?.name || "",
        };
      }),
    };
    onUserUpdate(updatedUser, updateMainDb);
  };

  const handleUndo = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setRole(user.globalRole);
    setSelectedClients(user.clientAccesses.map((client) => client.id));
  };

  const isFormChanged = () => {
    return (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      email !== user.email ||
      adObjectId !== user.adObjectId ||
      role !== user.globalRole ||
      JSON.stringify(selectedClients.sort()) !==
        JSON.stringify(user.clientAccesses.map((access) => access.clientId).sort())
    );
  };

  const submit = (action: Action) => {
    if (user.id > 0 && action === Action.CREATE) {
      action = Action.UPDATE;
    }
    confirmAlert({
      title: action + " user id " + user.id,
      message: `Are you sure you want to ${action} user ${firstName} ${lastName}?`,
      buttons: [
        {
          label: action,
          onClick:
            action === Action.CREATE || action === Action.UPDATE
              ? () => handleSave()
              : () => onUserDelete(user.id, updateMainDb),
        },
        {
          label: "Cancel",
        },
      ],
    });
  };

  return (
    <Box>
      <Card sx={{ padding: 2 }}>
        <h2>User with id {user.id}</h2>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <TextField fullWidth label="First Name" value={firstName} onChange={handleFirstNameChange} />
          </Grid>
          <Grid item>
            <TextField fullWidth label="Last Name" value={lastName} onChange={handleLastNameChange} />
          </Grid>
          <Grid item>
            <TextField fullWidth label="Email" value={email} onChange={handleEmailChange} />
          </Grid>
          <Grid item>
            <TextField fullWidth label="Ad Object Id" value={adObjectId} onChange={handleAdObjectIdChange} />
          </Grid>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={role || ""} label="Role" onChange={handleRoleChange}>
                {Object.values(UserRole).map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>Client access</InputLabel>
              <Select
                label="Client access"
                multiple
                value={selectedClients}
                onChange={handleTopicChange}
                renderValue={(selected) =>
                  selected.map((id) => clients.find((client) => client.id === id)?.name).join(", ")
                }
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Checkbox checked={selectedClients.indexOf(client.id) > -1} />
                    <ListItemText primary={client.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ marginRight: 1 }}
              color="primary"
              disabled={formValid() || !isFormChanged()}
              onClick={() => submit(Action.CREATE)}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={{ marginRight: 1 }}
              color="secondary"
              disabled={user.id < 0 || !isFormChanged()}
              onClick={handleUndo}
            >
              Undo
            </Button>
            <Button variant="contained" color="warning" onClick={() => submit(Action.DELETE)}>
              Delete
            </Button>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default UserForm;
